// GK Brandify — bespoke service emblem set (custom SVG line-art with gold accents)
import React from "react";

const GOLD = "#D4AF37";
const CHAMP = "#F5E6A3";

// Shared gradient defs; unique id per icon to avoid collisions
const Grad = ({ id }) => (
  <defs>
    <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor={CHAMP} />
      <stop offset="55%" stopColor={GOLD} />
      <stop offset="100%" stopColor="#997A15" />
    </linearGradient>
  </defs>
);

const base = {
  width: 26,
  height: 26,
  viewBox: "0 0 48 48",
  fill: "none",
  stroke: CHAMP,
  strokeWidth: 1.3,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

// 1. Logo Design — monogram mark inside a facet
const LogoDesign = (p) => (
  <svg {...base} {...p}><Grad id="ic-logo" />
    <circle cx="24" cy="24" r="16" stroke={CHAMP} opacity="0.35" />
    <path d="M30 17a8 8 0 1 0 0 14v-6h-5" stroke={`url(#ic-logo)`} strokeWidth="2" />
    <path d="M20 16l-4 16" opacity="0.5" />
  </svg>
);

// 2. Website Development — code brackets + cursor
const WebsiteDevelopment = (p) => (
  <svg {...base} {...p}><Grad id="ic-dev" />
    <rect x="7" y="11" width="34" height="26" rx="3" opacity="0.35" />
    <path d="M19 22l-4 3 4 3" stroke={`url(#ic-dev)`} strokeWidth="2" />
    <path d="M29 22l4 3-4 3" stroke={`url(#ic-dev)`} strokeWidth="2" />
    <path d="M26 20l-4 10" opacity="0.6" />
  </svg>
);

// 3. Website Design — layout / artboard
const WebsiteDesign = (p) => (
  <svg {...base} {...p}><Grad id="ic-web" />
    <rect x="8" y="9" width="32" height="30" rx="3" opacity="0.35" />
    <path d="M8 17h32" />
    <rect x="12" y="21" width="10" height="14" rx="1.5" stroke={`url(#ic-web)`} strokeWidth="1.8" />
    <path d="M26 22h10M26 27h10M26 32h6" opacity="0.6" />
    <circle cx="12" cy="13" r="1" fill={GOLD} stroke="none" />
  </svg>
);

// 4. Brand Identity — gem / diamond facets
const BrandIdentity = (p) => (
  <svg {...base} {...p}><Grad id="ic-brand" />
    <path d="M16 10h16l6 9-14 20L10 19z" stroke={`url(#ic-brand)`} strokeWidth="1.8" />
    <path d="M10 19h28M16 10l8 9 8-9M24 19v20" opacity="0.5" />
  </svg>
);

// 5. UI/UX — nested frames + pointer
const UIUX = (p) => (
  <svg {...base} {...p}><Grad id="ic-ux" />
    <rect x="8" y="8" width="24" height="24" rx="3" opacity="0.4" />
    <path d="M22 26l4 12 3-6 6-1z" fill={`url(#ic-ux)`} stroke={GOLD} strokeWidth="1.2" />
    <path d="M14 15h8M14 20h5" opacity="0.6" />
  </svg>
);

// 6. Mobile Apps — device + spark
const MobileApps = (p) => (
  <svg {...base} {...p}><Grad id="ic-mob" />
    <rect x="15" y="7" width="18" height="34" rx="4" opacity="0.4" />
    <path d="M22 11h4" opacity="0.7" />
    <path d="M24 24l1.6 3.4L29 29l-3.4 1.6L24 34l-1.6-3.4L19 29l3.4-1.6z" fill={`url(#ic-mob)`} stroke={GOLD} strokeWidth="1" />
  </svg>
);

// 7. Digital Marketing — megaphone / signal
const DigitalMarketing = (p) => (
  <svg {...base} {...p}><Grad id="ic-mkt" />
    <path d="M12 20v8l6 2v6h4v-4l14 6V12L22 18h-6a4 4 0 0 0-4 4z" stroke={`url(#ic-mkt)`} strokeWidth="1.6" />
    <path d="M38 21c2 1.2 2 4.8 0 6" opacity="0.6" />
  </svg>
);

// 8. SEO — magnifier + growth bars
const SEO = (p) => (
  <svg {...base} {...p}><Grad id="ic-seo" />
    <circle cx="21" cy="21" r="12" opacity="0.4" />
    <path d="M30 30l7 7" stroke={`url(#ic-seo)`} strokeWidth="2.2" />
    <path d="M16 24v-3M21 24v-6M26 24v-9" stroke={`url(#ic-seo)`} strokeWidth="2" />
  </svg>
);

// 9. AI Solutions — neural node network
const AISolutions = (p) => (
  <svg {...base} {...p}><Grad id="ic-ai" />
    <circle cx="24" cy="12" r="3" fill={`url(#ic-ai)`} stroke="none" />
    <circle cx="12" cy="30" r="3" stroke={CHAMP} />
    <circle cx="36" cy="30" r="3" stroke={CHAMP} />
    <circle cx="24" cy="24" r="4" stroke={`url(#ic-ai)`} strokeWidth="1.8" />
    <path d="M24 15v5M21 26l-6 3M27 26l6 3" opacity="0.6" />
  </svg>
);

// 10. Cloud Development — cloud + braces
const CloudDevelopment = (p) => (
  <svg {...base} {...p}><Grad id="ic-cloud" />
    <path d="M16 33a7 7 0 0 1-1-14 9 9 0 0 1 17 2 6 6 0 0 1 0 12z" opacity="0.4" />
    <path d="M20 22c-2 0-2 2-2 3s0 3-2 3c2 0 2 2 2 3s0 3 2 3" stroke={`url(#ic-cloud)`} strokeWidth="1.6" transform="translate(-1 0)" />
    <path d="M28 22c2 0 2 2 2 3s0 3 2 3c-2 0-2 2-2 3s0 3-2 3" stroke={`url(#ic-cloud)`} strokeWidth="1.6" transform="translate(1 0)" />
  </svg>
);

// 11. Google Business Profile — map pin + rating star
const GoogleBusiness = (p) => (
  <svg {...base} {...p}><Grad id="ic-gbp" />
    <path d="M24 8c-6 0-10 4.5-10 10.5C14 27 24 40 24 40s10-13 10-21.5C34 12.5 30 8 24 8z" stroke={`url(#ic-gbp)`} strokeWidth="1.6" />
    <path d="M24 14.5l1.8 3.8 4.2.5-3.1 2.9.8 4.2-3.7-2.1-3.7 2.1.8-4.2-3.1-2.9 4.2-.5z" fill={`url(#ic-gbp)`} stroke="none" />
  </svg>
);

// 12. Social Media Branding — chat + heart pulse
const SocialMedia = (p) => (
  <svg {...base} {...p}><Grad id="ic-social" />
    <path d="M10 12h28v18H24l-8 6v-6h-6z" opacity="0.4" />
    <path d="M24 27c-3-2-6-4-6-7a3 3 0 0 1 6-1 3 3 0 0 1 6 1c0 3-3 5-6 7z" fill={`url(#ic-social)`} stroke={GOLD} strokeWidth="1" />
  </svg>
);

// 13. Digital Marketing Consultation — growth chart + arrow
const MarketingConsult = (p) => (
  <svg {...base} {...p}><Grad id="ic-mktc" />
    <path d="M9 37V11" opacity="0.5" />
    <path d="M9 37h30" opacity="0.5" />
    <path d="M13 31l7-8 5 5 9-11" stroke={`url(#ic-mktc)`} strokeWidth="2" />
    <path d="M30 17h6v6" stroke={`url(#ic-mktc)`} strokeWidth="2" />
    <circle cx="20" cy="23" r="1.6" fill={GOLD} stroke="none" />
    <circle cx="25" cy="28" r="1.6" fill={GOLD} stroke="none" />
  </svg>
);

export const SERVICE_ICONS = {
  LogoDesign,
  WebsiteDevelopment,
  WebsiteDesign,
  BrandIdentity,
  UIUX,
  MobileApps,
  DigitalMarketing,
  SEO,
  AISolutions,
  CloudDevelopment,
  GoogleBusiness,
  SocialMedia,
  MarketingConsult,
};

export default SERVICE_ICONS;
